import { DistancePipe, DurationPipe } from '@motes-sdk/pipes';
import { Language } from '@motes-sdk/models/language'

/**
 * Class includes map of all text pieces used in the auto generated tour (tour.component.ts).
 * The text pieces exist in Danish, English, and Swedish.
 * To add a new piece, add it to the TextType class, and to each key of the map.
 * To add a new language, add it to the Translation enum, to the map and to getBringTime(), getWorkRoute() and getWorkCollection() - which are not included in the map
 */
export class TourInfoTranslation {

    private durationPipe: DurationPipe = new DurationPipe();
    private distancePipe: DistancePipe = new DistancePipe();

    private readonly textTranslationMap: Partial<Record<Language, TourPageTranslation>> = {
        'da': {
            subtitle: "Vil du udforske byen på en ny måde?",
            startTour: "Start tur",
            buyTour: "Køb tur",
            continueTour: "Fortsæt tur",
            unavailableTour: "Denne tur er ikke længere tilgængelig",
            loading: "Henter",
            seeSneakPeek: "Få en smagsprøve",
            tourDescTitle: "Hvad handler denne tur om?",
            workTitle: "Hvordan virker det?",
            workLocation: "StoryHunt app'en bruger din lokation til at guide dig mellem historier.",
            workUnlock: "Når du er tæt på en historie, kan du låse op for den.",
            workPodcast: "Dette er en lydtur, så husk dine hovedtelefoner.",
            workChallenge: "Denne tur indeholder en quiz – test din viden og dyst med dine venner undervejs!",
            workGroup: "Vi understøtter en fed gruppeoplevelse, så tag en ven med på tur.",
            bringTitle: "Hvad skal jeg have med?",
            bringSmartphone: "En opladt smartphone.",
            bringHeadphones: "Hovedtelefoner.",
            bringCompany: "Godt selskab er anbefalet.",
            getStarted: "Tag Afsted",
            visit: "Besøg vores app for at komme igang. Det er helt gratis.",
            explore: "Gå på opdagelse når du vil. Det er fuldstændig gratis.",
            openApp: "Start Tur",
            free: 'Gratis',
            stories: 'historier',
            sneakPeekHeader: 'Få en smagsprøve på turen her',
            watchVideoAbout: 'Se video om',
            seeRoute: 'Se tur på kort',
            buyUseAgain: 'Køb turen herunder. En købt tur kan bruges af én person igen og igen.',
            buyShare: 'Hvis du køber mere end én tur, kan du dele dem med dine venner.',
            buyBring: 'Tag ud af døren, tag hovedtelefonerne på og oplev turen på din smartphone.',
            creatorPersonal: 'Denne tur er skabt med et gratis abonnement i StoryHunt Creator.',
            creatorProfessional: 'Denne tur er skabt af en StoryHunt-partner.',
            creatorEnterprise: 'Denne tur er skabt af en StoryHunt-partner.',
            creatorInternal: 'Dette er en StoryHunt Original Production.',
            faq: 'FAQ',
            faqStartPlaceQ: 'Hvor starter turen?',
            faqDurationQ: 'Hvor lang er turen?',
            faqGiftQ: 'Kan jeg give turen i gave?',
            faqFreeQ: 'Er turen gratis?',
            faqFrequencyQ: 'Hvor mange gange kan jeg gå turen?',
            faqFriendsQ: 'Kan jeg tage venner med på turen?',
            faqStartTimeQ: 'Hvornår kan jeg gå turen?',
            faqStartPlaceCollectionA: () => `Du kan starte turen fra hvilken som helst af de ${this.storyCount} historier – du bestemmer hvor mange og i hvilken rækkefølge!`,
            faqStartPlaceRouteA: () => `Turen starter ved ${this.startPoint}. Herfra følger den ${this.storyCount} ${this.textTranslationMap[this.tourLanguage].stories}${this.endPoint ? ' og slutter ved ' + this.endPoint : ''}.`,
            faqDurationCollectionA: () => `Du bestemmer, hvor mange historier du vil låse op for, og dermed hvor lang turen bliver.`,
            faqDurationRouteA: () => `Turen er ${this.distancePipe.transform(this.tourDistance)} lang og tager typisk ${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} at gennemføre.`,
            faqGiftA: 'Ja. Når du har købt turen, modtager du en email, som du kan sende videre, til hvem du måtte ønske.',
            faqFreeA: 'Ja, turen er 100% gratis og kan opleves gennem StoryHunt-appen.',
            faqFrequencyA: 'Ubegrænset. Når først du har købt adgang til turen, kan du gå den så mange gange du vil.',
            faqFrequencyFreeA: 'Ubegrænset. Du kan gå den så mange gange du vil.',
            faqFriendsA: 'Ja. Når vennerne er med, kan I oprette en gruppe, så jeres oplevelse kan synkroniseres.',
            faqFriendsWithQuizA: 'Ja. Når vennerne er med, kan I oprette en gruppe, så jeres oplevelse kan synkroniseres, og I kan quizze mod hinanden.',
            faqStartTimeA: 'Når som helst. Du kan påbegynde turen lige når du har lyst.',
            bringTime: () => `${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} til at gennemføre turen.`,
            workCollection: () => `Du bestemmer selv rækkefølgen mellem de ${this.storyCount} historier i ${this.city}.`,
            workRoute: () => `Turen starter ved ${this.startPoint} og følger en fast rute mellem ${this.storyCount} historier.`,
        },
        'en': {
            subtitle: "Do you wanna explore the city in a new way?",
            startTour: "Start tour",
            buyTour: "Buy tour",
            continueTour: "Continue tour",
            unavailableTour: "This tour is no longer available",
            loading: "Loading",
            seeSneakPeek: "Get a sneak peek",
            tourDescTitle: "What is this tour about?",
            workTitle: "How does it work?",
            workLocation: "The StoryHunt app uses your location to guide you between stories.",
            workUnlock: "When you are close to a story you can unlock it to reveal its content.",
            workPodcast: "This is an audio tour so bring your headphones.",
            workChallenge: "This tour contains a quiz – test your knowledge and compete with your friends along the way!",
            workGroup: "We support a great group experience, so bring along your friends.",
            bringTitle: "What do I bring?",
            bringSmartphone: "A charged smartphone.",
            bringHeadphones: "Headphones.",
            bringCompany: "Good company is recommended.",
            getStarted: "Get Started",
            visit: "Visit our app to get started. It's completely free.",
            explore: "Go exploring anytime you want. It's completely free.",
            openApp: "Start Tour",
            free: 'Free',
            stories: 'stories',
            sneakPeekHeader: 'Sneak a peek',
            watchVideoAbout: 'Watch video about',
            seeRoute: 'See tour on map',
            buyUseAgain: 'Buy the tour below. A purchased tour can be used by the same person again and again.',
            buyShare: 'If you buy more than one tour, you can share your unused tours with your friends.',
            buyBring: 'Walk out the door, put on your headphones and experience the tour on your smartphone.',
            creatorPersonal: 'This tour is made with a free account in StoryHunt Creator.',
            creatorProfessional: 'This is created by a StoryHunt Partner.',
            creatorEnterprise: 'This is created by a StoryHunt Partner.',
            creatorInternal: 'This is a StoryHunt Original Production.',
            faq: 'FAQ',
            faqStartPlaceQ: 'Where does the tour start?',
            faqDurationQ: 'How long is the tour?',
            faqGiftQ: 'Can I give the tour as a gift?',
            faqFreeQ: 'Is the tour free?',
            faqFrequencyQ: 'How many times can I take the tour?',
            faqFriendsQ: 'Can I bring friends on the tour?',
            faqStartTimeQ: 'When can I take the tour?',
            faqStartPlaceCollectionA: () => `You can start your tour from any of the ${this.storyCount} stories – you choose how many and in which order!`,
            faqStartPlaceRouteA: () => `The tour starts at ${this.startPoint}. From there it follows ${this.storyCount} ${this.textTranslationMap[this.tourLanguage].stories}${this.endPoint ? ' and ends at ' + this.endPoint : ''}.`,
            faqDurationCollectionA: () => `You decide how many stories you want to unlock and thereby how long the tour is.`,
            faqDurationRouteA: () => `The tour is ${this.distancePipe.transform(this.tourDistance)} long and typically takes ${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} to complete.`,
            faqGiftA: 'Yes. Once you have purchased the tour you will receive an email that you can gift to anyone you like.',
            faqFreeA: 'Yes, the tour is 100% free and can be experienced through the StoryHunt app.',
            faqFrequencyA: 'Unlimited. You can take the tour as many times as you like once you have purchased access.',
            faqFrequencyFreeA: 'Unlimited. You can take it as many times as you want.',
            faqFriendsA: 'Yes. When you bring friends you can make a group to synchronize the experience.',
            faqFriendsWithQuizA: 'Yes. When you bring friends you can make a group to synchronize the experience and compete in quizzes.',
            faqStartTimeA: 'Whenever. You can take the tour anytime you want to.',
            bringTime: () =>  `${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} to complete the tour.`,
            workCollection: () => `You decide in which order you visit the ${this.storyCount} stories in ${this.city}.`,
            workRoute: () => `The tour starts at ${this.startPoint} and follows a predefined route between ${this.storyCount} stories.`,
        },
        'sv': {
            subtitle: "Vill du utforska staden på ett nytt sätt?",
            startTour: "Starta turen",
            buyTour: "Köp turen",
            continueTour: "Fortsätt tur",
            unavailableTour: "Denna tur är inte längre tillgänglig",
            loading: "Laddar",
            seeSneakPeek: "Få en försmak",
            tourDescTitle: "Vad är den här turen om?",
            workTitle: "Hur fungerar det?",
            workLocation: "StoryHunt-appen använder din plats för att vägleda dig mellan berättelserna.",
            workUnlock: "När du är nära en berättelse kan du låsa upp den.",
            workPodcast: "Detta är en ljudturen så kom ihåg dina hörlurar.",
            workChallenge: "Denna turné innehåller en frågesport – testa dina kunskaper och tävla med dina vänner på vägen!",
            workGroup: "Vi stödjer en fantastisk gruppupplevelse, så ta med en vän på turen.",
            bringTitle: "Vad tar jag med mig?",
            bringSmartphone: "En laddad smartphone.",
            bringHeadphones: "Hörlurar.",
            bringCompany: "Bra sällskap rekommenderas",
            getStarted: "Komma igång",
            visit: "Besök vår app för att komma igång. Det är helt gratis.",
            explore: "Utforska när som helst du vill. Det är helt gratis.",
            openApp: "Start",
            free: 'Gratis',
            stories: 'berättelser',
            sneakPeekHeader: 'Få en försmak av turen hit',
            watchVideoAbout: 'Titta på video om',
            seeRoute: 'Se tur på kartan',
            buyUseAgain: 'Köp turen nedan. En köpt tur kan användas av en person om och om igen.',
            buyShare: 'Om du köper mer än en tur kan du dela dem med dina vänner.',
            buyBring: 'Gå ut genom dörren, sätt på dig hörlurarna och upplev turen på din smartphone.',
            creatorPersonal: 'Denna turné skapades med en gratis prenumeration i StoryHunt Creator.',
            creatorProfessional: 'Denna tur skapades av en StoryHunt-partner.',
            creatorEnterprise: 'Denna tur skapades av en StoryHunt-partner.',
            creatorInternal: 'Detta är en StoryHunt Original Production.',
            faq: 'FAQ',
            faqStartPlaceQ: 'Var börjar turen?',
            faqDurationQ: 'Hur lång är turen?',
            faqGiftQ: 'Kan jag ge turen som present?',
            faqFreeQ: 'Är turen gratis?',
            faqFrequencyQ: 'Hur många gånger kan jag ta turen?',
            faqFriendsQ: 'Kan jag ta med vänner på turen?',
            faqStartTimeQ: 'När kan jag ta turen?',
            faqStartPlaceCollectionA: () => `Du kan börja turen från vilken som helst av de ${this.storyCount} historierna – du bestämmer hur många och i vilken ordning!`,
            faqStartPlaceRouteA: () => `Turen börjar vid ${this.startPoint}. Därifrån följer den ${this.storyCount} ${this.textTranslationMap[this.tourLanguage].stories}${this.endPoint ? ' och slutar vid ' + this.endPoint : ''}.`,
            faqDurationCollectionA: () => `Du bestämmer hur många historier du vill låsa upp och därmed hur lång turen blir.`,
            faqDurationRouteA: () => `Turen är ${this.distancePipe.transform(this.tourDistance)} lång och tar vanligtvis ${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} att genomföra.`,
            faqGiftA: 'Ja. När du har köpt turen får du ett e-postmeddelande som du kan skicka vidare till vem du vill.',
            faqFreeA: 'Ja, turen är 100% gratis och kan upplevas genom StoryHunt-appen.',
            faqFrequencyA: 'Obegränsat. När du väl har köpt tillgång till turen kan du gå den så många gånger du vill.',
            faqFrequencyFreeA: 'Obegränsad. Du kan gå den så många gånger du vill.',
            faqFriendsA: 'Ja. När dina vänner är med kan ni skapa en grupp så att er upplevelse kan synkroniseras.',
            faqFriendsWithQuizA: 'Ja. När dina vänner är med kan ni skapa en grupp så att er upplevelse kan synkroniseras och ni kan quizza mot varandra.',
            faqStartTimeA: 'När som helst. Du kan börja turen när du vill.',
            bringTime: () =>  `${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} att slutföra turen.`,
            workCollection: () => `Du bestämmer ordningen mellan de ${this.storyCount} berättelser i ${this.city}.`,
            workRoute: () => `Turen startar vid ${this.startPoint} och följer en fast rutt mellan ${this.storyCount} berättelser.`,
        },
        'de': {
            subtitle: "Möchten Sie die Stadt auf eine neue Art erkunden?",
            startTour: "Starten Sie die Tour",
            buyTour: "Kaufen Sie die Tour",
            continueTour: "Tour fortsetzen",
            unavailableTour: "Diese Tour ist nicht mehr verfügbar",
            loading: "Wird geladen",
            seeSneakPeek: "Einen Vorgeschmack",
            tourDescTitle: "Worüber handelt die Tour?",
            workTitle: "Wie funktioniert es?",
            workLocation: "Die App tracked Ihren Standort und führt Sie durch die Geschichten.",
            workUnlock: "Wenn Sie sich in der Nähe einer Geschichte befinden, können Sie sie freischalten.",
            workPodcast: "Dies ist eine Audiotour, bringen Sie also Ihre Kopfhörer mit.",
            workChallenge: "Diese Tour enthält ein Quiz – testen Sie Ihr Wissen und messen Sie sich unterwegs mit Ihren Freunden!",
            workGroup: "Dies ist eine tolle Gruppenerfahrungen, bringen Sie gerne Bekannte",
            bringTitle: "Was soll ich mitbringen?",
            bringSmartphone: "Ein aufgelandenes Smartphone.",
            bringHeadphones: "Kopfhöhrer.",
            bringCompany: "Gute Begleitung ist empfohlen.",
            getStarted: "Los gehts",
            visit: "Besuchen Sie unsere App um zu starten. Es ist kostenlos.",
            explore: "Erkunde jederzeit, wenn du möchtest. Es ist komplett kostenlos.",
            openApp: "Starten Sie die Tour",
            free: 'Kostenlos',
            stories: 'Geschichten',
            sneakPeekHeader: 'Einen Vorgeschmack bekommen',
            watchVideoAbout: 'Schauen Sie das Video um',
            seeRoute: 'Siehe Tour auf der Karte',
            buyUseAgain: 'Kaufen Sie die unten stehende Tour. Eine gekaufte Tour kann von der gleichen Person mehrmals genutzt werden.',
            buyShare: 'Wenn Sie mehr als eine Tour kaufen, können Sie ungenutzte Touren an Ihre Freunden weiterleiten.',
            buyBring: 'Gehen Sie mit Ihren Kopfhörern raus und erfahren Sie die Tour auf Ihrem Smartphone.',
            creatorPersonal: 'Diese Tour wurde mit einem kostenlosen Abonnement in StoryHunt Creator kreiert.',
            creatorProfessional: 'Diese Tour von einem StoryHunt Partner kreiert.',
            creatorEnterprise: 'Diese Tour von einem StoryHunt Partner kreiert.',
            creatorInternal: 'Das ist eine originale StoryHunt Produktion.',
            faq: 'FAQ',
            faqStartPlaceQ: 'Wo beginnt die Tour?',
            faqDurationQ: 'Wie lange dauert die Tour?',
            faqGiftQ: 'Kann ich die Tour verschenken?',
            faqFreeQ: 'Ist die Tour kostenlos?',
            faqFrequencyQ: 'Wie oft kann ich die Tour machen?',
            faqFriendsQ: 'Kann ich Freunde mit auf die Tour nehmen?',
            faqStartTimeQ: 'Wann kann ich die Tour machen?',
            faqStartPlaceCollectionA: () => `Du kannst die Tour von jeder der ${this.storyCount} Geschichten aus starten - du entscheidest, wie viele und in welcher Reihenfolge!`,
            faqStartPlaceRouteA: () => `Die Tour beginnt bei ${this.startPoint}. Von dort aus folgt sie ${this.storyCount} ${this.textTranslationMap[this.tourLanguage].stories}${this.endPoint ? ' und endet bei ' + this.endPoint : ''}.`,
            faqDurationCollectionA: () => `Du entscheidest, wie viele Geschichten du freischalten möchtest und damit wie lang die Tour wird.`,
            faqDurationRouteA: () => `Die Tour ist ${this.distancePipe.transform(this.tourDistance)} lang und dauert in der Regel ${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} um sie abzuschließen.`,
            faqGiftA: 'Ja. Sobald du die Tour gekauft hast, erhältst du eine E-Mail, die du an wen auch immer du möchtest weiterleiten kannst.',
            faqFreeA: 'Ja, die Tour ist 100% kostenlos und kann über die StoryHunt-App erlebt werden.',
            faqFrequencyA: 'Unbegrenzt. Sobald du Zugang zur Tour hast, kannst du sie so oft machen, wie du möchtest.',
            faqFrequencyFreeA: 'Unbegrenzt. Du kannst sie so oft machen, wie du möchtest.',
            faqFriendsA: 'Ja. Wenn deine Freunde mitmachen, könnt ihr eine Gruppe erstellen, damit eure Erfahrungen synchronisiert werden können.',
            faqFriendsWithQuizA: 'Ja. Wenn deine Freunde mitmachen, könnt ihr eine Gruppe erstellen, damit eure Erfahrungen synchronisiert werden können und ihr euch gegenseitig quizzen könnt.',
            faqStartTimeA: 'Jederzeit. Du kannst die Tour beginnen, wann immer du willst.',
            bringTime: () => `${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} um die Tour abzuschließen.`,
            workCollection: () => `Sie bestimmen die Reihenfolge zwischen den ${this.storyCount} Geschichten in ${this.city}.`,
            workRoute: () => `Die Tour beginnt am ${this.startPoint} und folgt einer festgelegten Route zwischen ${this.storyCount} Geschichten.`,
        },
        'es': {
            subtitle: "¿Quieres explorar la ciudad de una manera nueva?",
            startTour: "Comienza tour",
            buyTour: "Comprar el tour",
            continueTour: "Continuar recorrido",
            unavailableTour: "Este recorrido ya no está disponible",
            loading: "Cargando",
            seeSneakPeek: "Escucha un avance del tour",
            tourDescTitle: "¿De qué se trata este tour?",
            workTitle: "¿Cómo funciona?",
            workLocation: "La app usa tu localización para guiarte entre los puntos de interés.",
            workUnlock: "Cuando estés cerca de una historia puedes desbloquearla.",
            workPodcast: "Esto es un tour audioguiado, así que acuérdate de traer tus auriculares.",
            workChallenge: "Este tour tiene un pequeño cuestionario para comprobar qué has aprendido - puedes competir con tus amigos a lo largo del tour.",
            workGroup: "Apoyamos una experiencia grupal divertida, así que lleva a un amigo contigo en el viaje.",
            bringTitle: "¿Qué debo llevar?",
            bringSmartphone: "Una teléfono inteligente cargada.",
            bringHeadphones: "Auriculares.",
            bringCompany: "Un buen compañía es recomendado.",
            getStarted: "¡Sal a descubrir!",
            visit: "Visita nuestra app para empezar. Es totalmente gratuita.",
            explore: "Explora en cualquier momento que desees. Es completamente gratis.",
            openApp: "Empezar el tour",
            free: 'Gratis',
            stories: 'historias',
            sneakPeekHeader: 'Oye pequeños fragmentos del tour',
            watchVideoAbout: 'Mira el video',
            seeRoute: 'Vea el tour en el mapa',
            buyUseAgain: 'Cuando una persona compra un tour, lo puede usar tantas veces como quiera.',
            buyShare: 'Sí compras más de un tour, puedes compartirlos con tus amigos.',
            buyBring: '¡Sal afuera, ponte los auriculares y vive el tour en tu smartphone!',
            creatorPersonal: 'Este tour se crea con una suscripción gratuita en StoryHunt Creator.',
            creatorProfessional: 'Esta tour fue creada por un socio de StoryHunt.',
            creatorEnterprise: 'Esta tour fue creada por un socio de StoryHunt.',
            creatorInternal: 'Esto es una producción original de Storyhunt.',
            faq: 'Preguntas frecuentes',
            faqStartPlaceQ: '¿Dónde comienza el tour?',
            faqDurationQ: '¿Cuánto dura el tour?',
            faqGiftQ: '¿Puedo regalar el tour?',
            faqFreeQ: '¿Es el tour gratis?',
            faqFrequencyQ: '¿Cuántas veces puedo hacer el tour?',
            faqFriendsQ: '¿Puedo llevar amigos en el tour?',
            faqStartTimeQ: '¿Cuándo puedo hacer el tour?',
            faqStartPlaceCollectionA: () => `Puedes comenzar el recorrido desde cualquiera de las ${this.storyCount} historias: ¡tú decides cuántas y en qué orden!`,
            faqStartPlaceRouteA: () => `El recorrido comienza en ${this.startPoint}. Desde allí sigue ${this.storyCount} ${this.textTranslationMap[this.tourLanguage].stories}${this.endPoint ? ' y termina en ' + this.endPoint : ''}.`,
            faqDurationCollectionA: () => `Tú decides cuántas historias desbloquear y, por lo tanto, cuánto durará el recorrido.`,
            faqDurationRouteA: () => `El recorrido tiene una longitud de ${this.distancePipe.transform(this.tourDistance)} y, por lo general, tarda ${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} en completarse.`,
            faqGiftA: 'Sí. Una vez que compres el recorrido, recibirás un correo electrónico que podrás enviar a quien desees.',
            faqFreeA: 'Sí, el tour es 100% gratis y se puede experimentar a través de la aplicación StoryHunt.',
            faqFrequencyA: 'Ilimitado. Una vez que tengas acceso al recorrido, puedes hacerlo tantas veces como quieras.',
            faqFrequencyFreeA: 'Ilimitado. Puedes hacerlo tantas veces como quieras.',
            faqFriendsA: 'Sí. Cuando tus amigos se unan, pueden crear un grupo para sincronizar sus experiencias.',
            faqFriendsWithQuizA: 'Sí. Cuando tus amigos se unan, pueden crear un grupo para sincronizar sus experiencias y hacerse preguntas entre ellos.',
            faqStartTimeA: 'En cualquier momento. Puedes comenzar el recorrido cuando quieras.',
            bringTime: () => `${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} para completar el tour.`,
            workCollection: () => `Tú decides el orden entre las ${this.storyCount} historias de ${this.city}.`,
            workRoute: () => `El tour empieza en ${this.startPoint} y sigue una ruta fija pasando por ${this.storyCount} puntos de interés.`,
        },
        "fr": {
            subtitle: "Voulez-vous explorer la ville d'une nouvelle façon?",
            startTour: "Commencer la visite",
            buyTour: "Acheter la visite",
            continueTour: "Continuer la visite",
            unavailableTour: "Cette visite n'est plus disponible",
            loading: "Chargement",
            seeSneakPeek: "Obtenir un aperçu",
            tourDescTitle: "De quoi parle cette visite?",
            workTitle: "Comment ça marche?",
            workLocation: "L'application StoryHunt utilise votre position pour vous guider entre les histoires.",
            workUnlock: "Lorsque vous êtes proche d'une histoire, vous pouvez la déverrouiller pour révéler son contenu.",
            workPodcast: "Il s'agit d'une visite audio, alors apportez vos écouteurs.",
            workChallenge: "Cette visite contient un quiz - testez vos connaissances et rivalisez avec vos amis tout au long du chemin!",
            workGroup: "Nous soutenons une excellente expérience de groupe, alors amenez vos amis avec vous.",
            bringTitle: "Que dois-je apporter?",
            bringSmartphone: "Un smartphone chargé.",
            bringHeadphones: "Des écouteurs.",
            bringCompany: "Une bonne compagnie est recommandée.",
            getStarted: "Commencer",
            visit: "Visitez notre application pour commencer. C'est complètement gratuit.",
            explore: "Explorez à tout moment selon vos envies. C'est totalement gratuit.",
            openApp: "Commencer la visite",
            free: "Gratuit",
            stories: "histoires",
            sneakPeekHeader: "Jetez un coup d'œil",
            watchVideoAbout: "Regarder une vidéo à propos de",
            seeRoute: "Voir la visite sur la carte",
            buyUseAgain: "Achetez la visite ci-dessous. Une visite achetée peut être utilisée encore et encore par la même personne.",
            buyShare: "Si vous achetez plus d'une visite, vous pouvez partager vos visites inutilisées avec vos amis.",
            buyBring: "Sortez de la porte, mettez vos écouteurs et vivez la visite sur votre smartphone.",
            creatorPersonal: 'Cette visite est créée avec un compte gratuit dans StoryHunt Creator.',
            creatorProfessional: 'Cette visite a été créée par un partenaire de StoryHunt.',
            creatorEnterprise: 'Cette visite a été créée par un partenaire de StoryHunt.',
            creatorInternal: 'Ceci est une production originale de StoryHunt.',
            faq: 'FAQ',
            faqStartPlaceQ: 'Où commence la visite guidée?',
            faqDurationQ: 'Combien de temps dure la visite guidée?',
            faqGiftQ: 'Puis-je offrir la visite guidée en cadeau?',
            faqFreeQ: 'Est-ce que la visite est gratuite?',
            faqFrequencyQ: 'Combien de fois puis-je faire la visite guidée?',
            faqFriendsQ: 'Puis-je amener des amis lors de la visite guidée?',
            faqStartTimeQ: 'Quand puis-je faire la visite guidée?',
            faqStartPlaceCollectionA: () => `Vous pouvez commencer votre visite à partir de n'importe quelle des ${this.storyCount} histoires - vous choisissez combien et dans quel ordre !`,
            faqStartPlaceRouteA: () => `La visite commence à ${this.startPoint}. À partir de là, elle suit ${this.storyCount} ${this.textTranslationMap[this.tourLanguage].stories}${this.endPoint ? ' et se termine à ' + this.endPoint : ''}.`,
            faqDurationCollectionA: () => `Vous décidez du nombre d'histoires que vous souhaitez déverrouiller et ainsi de la durée de la visite.`,
            faqDurationRouteA: () => `La visite mesure ${this.distancePipe.transform(this.tourDistance)} de long et prend généralement ${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} pour être complétée.`,
            faqGiftA: 'Oui. Une fois que vous avez acheté la visite, vous recevrez un e-mail que vous pouvez offrir à qui vous voulez.',
            faqFreeA: "Oui, la visite est 100% gratuite et peut être réalisée via l'application StoryHunt.",
            faqFrequencyA: "Illimité. Vous pouvez faire la visite autant de fois que vous le souhaitez une fois que vous avez acheté l'accès.",
            faqFrequencyFreeA: 'Illimité. Vous pouvez la faire autant de fois que vous le souhaitez.',
            faqFriendsA: "Oui. Lorsque vous invitez des amis, vous pouvez créer un groupe pour synchroniser l'expérience",
            faqFriendsWithQuizA: "Oui. Lorsque vous invitez des amis, vous pouvez créer un groupe pour synchroniser l'expérience et participer à des quiz.",
            faqStartTimeA: "Quand vous le souhaitez. Vous pouvez commencer la visite à tout moment.",
            bringTime: () => `${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} pour compléter la visite.`,
            workCollection: () => `Vous décidez dans quel ordre vous visitez les ${this.storyCount} histoires à ${this.city}.`,
            workRoute: () => `La visite commence à ${this.startPoint} et suit un itinéraire prédéfini entre ${this.storyCount} histoires.`,
        },
        "no": {
            subtitle: "Vil du utforske byen på en ny måte?",
            startTour: "Start tur",
            buyTour: "Kjøp tur",
            continueTour: "Fortsett tur",
            unavailableTour: "Denne turen er ikke lenger tilgjengelig",
            loading: "Laster inn",
            seeSneakPeek: "Få en smakebit",
            tourDescTitle: "Hva handler denne turen om?",
            workTitle: "Hvordan fungerer det?",
            workLocation: "StoryHunt-appen bruker din lokasjon til å guide deg mellom historiene.",
            workUnlock: "Når du er nær en historie, kan du låse den opp.",
            workPodcast: "Dette er en lydtur, så husk hodetelefonene dine.",
            workChallenge: "Denne turen inneholder en quiz - test kunnskapen din og konkurrer med vennene dine underveis!",
            workGroup: "Vi støtter en morsom gruppeopplevelse, så ta med en venn på turen.",
            bringTitle: "Hva skal jeg ta med?",
            bringSmartphone: "En oppladet smarttelefon.",
            bringHeadphones: "Hodetelefoner.",
            bringCompany: "Godt selskap anbefales.",
            getStarted: "Kom i gang",
            visit: "Besøk appen vår for å komme i gang. Det er helt gratis.",
            explore: "Utforsk når som helst du ønsker. Det er helt gratis.",
            openApp: "Start Tur",
            free: 'Gratis',
            stories: 'historier',
            sneakPeekHeader: 'Få en smakebit på turen her',
            watchVideoAbout: 'Se video om',
            seeRoute: 'Se tur på kart',
            buyUseAgain: 'Kjøp turen nedenfor. En kjøpt tur kan brukes av én person igjen og igjen.',
            buyShare: 'Hvis du kjøper mer enn én tur, kan du dele dem med vennene dine.',
            buyBring: 'Gå ut døren, ta på hodetelefonene og opplev turen på smarttelefonen din.',
            creatorPersonal: 'Denne turen ble skapt med et gratis abonnement i StoryHunt Creator.',
            creatorProfessional: 'Denne turen ble skapt av en StoryHunt-partner.',
            creatorEnterprise: 'Denne turen ble skapt av en StoryHunt-partner.',
            creatorInternal: 'Dette er en originalproduksjon fra StoryHunt.',
            faq: 'FAQ',
            faqStartPlaceQ: 'Hvor begynner turen?',
            faqDurationQ: 'Hvor lang er turen?',
            faqGiftQ: 'Kan jeg gi turen som gave?',
            faqFreeQ: 'Er turen gratis?',
            faqFrequencyQ: 'Hvor mange ganger kan jeg ta turen?',
            faqFriendsQ: 'Kan jeg ta med venner på turen?',
            faqStartTimeQ: 'Når kan jeg ta turen?',
            faqStartPlaceCollectionA: () => `Du kan starte turen fra hvilken som helst av de ${this.storyCount} historiene - du velger antall og rekkefølge!`,
            faqStartPlaceRouteA: () => `Turen starter ved ${this.startPoint}. Derfra følger den ${this.storyCount} ${this.textTranslationMap[this.tourLanguage].stories}${this.endPoint ? ' og ender ved ' + this.endPoint : ''}.`,
            faqDurationCollectionA: () => `Du bestemmer hvor mange historier du vil låse opp og dermed hvor lang turen blir.`,
            faqDurationRouteA: () => `Turen er ${this.distancePipe.transform(this.tourDistance)} lang og tar vanligvis ${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} å fullføre.`,
            faqGiftA: 'Ja. Når du har kjøpt turen, mottar du en e-post som du kan gi videre til hvem du vil.',
            faqFreeA: 'Ja, turen er 100% gratis og kan oppleves gjennom StoryHunt-appen.',
            faqFrequencyA: 'Ubegrenset. Du kan ta turen så mange ganger du vil etter at du har kjøpt tilgang.',
            faqFrequencyFreeA: 'Ubegrenset. Du kan gå den så mange ganger du vil.',
            faqFriendsA: 'Ja. Når du tar med venner kan dere opprette en gruppe for å synkronisere opplevelsen.',
            faqFriendsWithQuizA: 'Ja. Når du tar med venner kan dere opprette en gruppe for å synkronisere opplevelsen og konkurrere i quizer.',
            faqStartTimeA: 'Når som helst. Du kan ta turen når som helst.',
            bringTime: () => `${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} å fullføre turen.`,
            workCollection: () => `Du bestemmer rekkefølgen mellom de ${this.storyCount} historiene i ${this.city}.`,
            workRoute: () => `Turen starter ved ${this.startPoint} og følger en fast rute mellem ${this.storyCount} historier.`,
        },
        'it': {
            subtitle: "Vuoi esplorare la città in un modo nuovo?",
            startTour: "Inizia il tour",
            buyTour: "Compra il tour",
            continueTour: "Continue tour",
            unavailableTour: "Questo tour non è più disponibile",
            loading: "Caricamento",
            seeSneakPeek: "Dai un'occhiata",
            tourDescTitle: "Di cosa tratta questo tour?",
            workTitle: "Come funziona?",
            workLocation: "L'app StoryHunt utilizza la tua posizione per guidarti tra le storie.",
            workUnlock: "Quando sei vicino a una storia puoi sbloccarla per rivelarne il contenuto.",
            workPodcast: "Questo è un tour audio quindi porta le tue cuffie.",
            workChallenge: "Questo tour contiene un quiz: metti alla prova le tue conoscenze e competi con i tuoi amici lungo il percorso!",
            workGroup: "Sosteniamo una grande esperienza di gruppo, quindi porta con te i tuoi amici.",
            bringTitle: "Cosa devo portare?",
            bringSmartphone: "Uno smartphone carico.",
            bringHeadphones: "Cuffie.",
            bringCompany: "Si consiglia una buona compagnia.",
            getStarted: "Inizia",
            visit: "Visita la nostra app per iniziare. È completamente gratuito.",
            explore: "Vai a esplorare quando vuoi. È completamente gratuito.",
            openApp: "Inizia il tour",
            free: 'Gratuito',
            stories: 'storie',
            sneakPeekHeader: 'Dai un\'occhiata',
            watchVideoAbout: 'Guarda il video su',
            seeRoute: 'Vedi il tour sulla mappa',
            buyUseAgain: 'Compra il tour qui sotto. Un tour acquistato può essere utilizzato dalla stessa persona più volte.',
            buyShare: 'Se acquisti più di un tour, puoi condividere i tuoi tour non utilizzati con i tuoi amici.',
            buyBring: 'Esci dalla porta, metti le cuffie e vivi il tour sul tuo smartphone.',
            creatorPersonal: 'Questo tour è stato realizzato con un account gratuito di StoryHunt Creator.',
            creatorProfessional: 'Questo è stato creato da un Partner di StoryHunt.',
            creatorEnterprise: 'Questo è stato creato da un Partner di StoryHunt.',
            creatorInternal: 'Questa è una produzione originale di StoryHunt.',
            faq: 'FAQ',
            faqStartPlaceQ: 'Dove inizia il tour?',
            faqDurationQ: 'Quanto dura il tour?',
            faqGiftQ: 'Posso regalare il tour?',
            faqFreeQ: 'Il tour è gratuito?',
            faqFrequencyQ: 'Quante volte posso fare il tour?',
            faqFriendsQ: 'Posso portare amici al tour?',
            faqStartTimeQ: 'Quando posso fare il tour?',
            faqStartPlaceCollectionA: () => `Puoi iniziare il tuo tour da ognuna delle ${this.storyCount} storie: tu scegli quante e in quale ordine!`,
            faqStartPlaceRouteA: () => `Il tour inizia a ${this.startPoint}. Da lì segue ${this.storyCount} ${this.textTranslationMap['it'].stories}.`,
            faqDurationCollectionA: () => `Tu decidi quante storie vuoi sbloccare e quindi quanto dura il tour.`,
            faqDurationRouteA: () => `Il tour è lungo ${this.distancePipe.transform(this.tourDistance)} e di solito ci vogliono ${this.durationPipe.transform(this.tourDuration, false, false, 60, 'it')} per completarlo.`,
            faqGiftA: 'Sì. Una volta acquistato il tour riceverai un\'email che puoi regalare a chiunque tu voglia.',
            faqFreeA: 'Sì, il tour è 100% gratuito e può essere sperimentato attraverso l\'app StoryHunt.',
            faqFrequencyA: 'Illimitato. Puoi fare il tour tutte le volte che vuoi una volta che hai acquistato l\'accesso.',
            faqFrequencyFreeA: 'Illimitato. Puoi farlo tutte le volte che vuoi.',
            faqFriendsA: 'Sì. Quando porti degli amici puoi creare un gruppo per sincronizzare l\'esperienza.',
            faqFriendsWithQuizA: 'Sì. Quando porti degli amici puoi creare un gruppo per sincronizzare l\'esperienza e competere nei quiz.',
            faqStartTimeA: 'Quando vuoi. Puoi fare il tour quando vuoi.',
            bringTime: () =>  `${this.durationPipe.transform(this.tourDuration, false, false, 60, 'it')} per completare il tour.`,
            workCollection: () => `Decidi in quale ordine visitare le ${this.storyCount} storie in ${this.city}.`,
            workRoute: () => `Il tour inizia a ${this.startPoint} e segue un percorso predefinito tra le ${this.storyCount} storie.`,
        },
        'nl': {
            subtitle: "Wil je de stad op een nieuwe manier ontdekken?",
            startTour: "Start tour",
            buyTour: "Koop tour",
            continueTour: "Ga verder met tour",
            unavailableTour: "Deze tour is niet meer beschikbaar",
            loading: "Laden",
            seeSneakPeek: "Bekijk een voorproefje",
            tourDescTitle: "Waar gaat deze tour over?",
            workTitle: "Hoe werkt het?",
            workLocation: "De StoryHunt-app gebruikt je locatie om je tussen de verhalen te begeleiden.",
            workUnlock: "Wanneer je dicht bij een verhaal bent, kun je het ontgrendelen om de inhoud te bekijken.",
            workPodcast: "Dit is een audiotour, dus neem je koptelefoon mee.",
            workChallenge: "Deze tour bevat een quiz – test je kennis en neem het op tegen je vrienden onderweg!",
            workGroup: "We ondersteunen een geweldige groepservaring, dus neem je vrienden mee.",
            bringTitle: "Wat moet ik meenemen?",
            bringSmartphone: "Een opgeladen smartphone.",
            bringHeadphones: "Een koptelefoon.",
            bringCompany: "Goed gezelschap wordt aanbevolen.",
            getStarted: "Aan de slag",
            visit: "Bezoek onze app om te beginnen. Het is helemaal gratis.",
            explore: "Ga op ontdekking wanneer je maar wilt. Het is helemaal gratis.",
            openApp: "Start Tour",
            free: "Gratis",
            stories: "verhalen",
            sneakPeekHeader: "Kijk stiekem mee",
            watchVideoAbout: "Bekijk video over",
            seeRoute: "Bekijk de tour op de kaart",
            buyUseAgain: "Koop de tour hieronder. Een gekochte tour kan steeds opnieuw door dezelfde persoon worden gebruikt.",
            buyShare: "Als je meer dan één tour koopt, kun je ongebruikte tours delen met je vrienden.",
            buyBring: "Ga naar buiten, zet je koptelefoon op en beleef de tour op je smartphone.",
            creatorPersonal: "Deze tour is gemaakt met een gratis account in StoryHunt Creator.",
            creatorProfessional: "Deze tour is gemaakt door een StoryHunt-partner.",
            creatorEnterprise: "Deze tour is gemaakt door een StoryHunt-partner.",
            creatorInternal: "Dit is een originele productie van StoryHunt.",
            faq: "FAQ",
            faqStartPlaceQ: "Waar begint de tour?",
            faqDurationQ: "Hoe lang duurt de tour?",
            faqGiftQ: "Kan ik de tour cadeau geven?",
            faqFreeQ: "Is de tour gratis?",
            faqFrequencyQ: "Hoe vaak kan ik de tour doen?",
            faqFriendsQ: "Kan ik vrienden meenemen op de tour?",
            faqStartTimeQ: "Wanneer kan ik de tour doen?",
            faqStartPlaceCollectionA: () => `Je kunt je tour starten bij elk van de ${this.storyCount} verhalen – jij kiest hoeveel en in welke volgorde!`,
            faqStartPlaceRouteA: () => `De tour start bij ${this.startPoint}. Vanaf daar volgt het ${this.storyCount} ${this.textTranslationMap[this.tourLanguage].stories}${this.endPoint ? ' en eindigt bij ' + this.endPoint : ''}.`,
            faqDurationCollectionA: () => `Jij bepaalt hoeveel verhalen je wilt ontgrendelen en dus hoe lang de tour duurt.`,
            faqDurationRouteA: () => `De tour is ${this.distancePipe.transform(this.tourDistance)} lang en duurt meestal ${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} om te voltooien.`,
            faqGiftA: "Ja. Nadat je de tour hebt gekocht ontvang je een e-mail die je aan iemand anders kunt geven.",
            faqFreeA: "Ja, de tour is 100% gratis en te beleven via de StoryHunt-app.",
            faqFrequencyA: "Onbeperkt. Je kunt de tour zo vaak doen als je wilt nadat je toegang hebt gekocht.",
            faqFrequencyFreeA: "Onbeperkt. Je kunt hem zo vaak doen als je wilt.",
            faqFriendsA: "Ja. Als je vrienden meeneemt, kun je een groep vormen om de ervaring te synchroniseren.",
            faqFriendsWithQuizA: "Ja. Als je vrienden meeneemt, kun je een groep vormen om samen te spelen en quizzen te doen.",
            faqStartTimeA: "Je kunt de tour op elk moment doen. Deze is 24 uur per dag, 7 dagen per week beschikbaar, het hele jaar door.",
            bringTime: () => `${this.durationPipe.transform(this.tourDuration, false, false, 60, this.tourLanguage)} om de tour te voltooien.`,
            workCollection: () => `Je bepaalt zelf in welke volgorde je de ${this.storyCount} verhalen in ${this.city} bezoekt.`,
            workRoute: () => `De tour start bij ${this.startPoint} en volgt een vooraf bepaalde route langs ${this.storyCount} verhalen.`,
        },
    }

    constructor(private tourLanguage: Language, private tourDuration: number | number[], private tourDistance: number, private startPoint: string, private endPoint: string, private storyCount: number, private city: string) {}

    public getText(textName: keyof TourPageTranslation) : string {
        // Get translation map or return default english translation if tour language does not exist
        const translation = this.textTranslationMap[this.tourLanguage] || this.textTranslationMap.en;
        if(!translation) return '';
        const textEntry = translation[textName];
        // Return text entry from map or getter
        return (typeof textEntry == 'string') ? textEntry : textEntry();
    }
}

/**
 * Identifiers for the different text pieces used in the auto generated tour
 */
interface TourPageTranslation {
    subtitle: string;
    startTour: string;
    buyTour: string;
    continueTour: string;
    unavailableTour: string;
    loading: string;
    seeSneakPeek: string;
    tourDescTitle: string;
    workTitle: string;
    workLocation: string;
    workUnlock: string;
    workPodcast: string;
    workChallenge: string;
    workGroup: string;
    bringTitle: string;
    bringSmartphone: string;
    bringHeadphones: string;
    bringCompany: string;
    getStarted: string;
    visit: string;
    explore: string;
    openApp: string;
    free: string;
    stories: string;
    sneakPeekHeader: string;
    watchVideoAbout: string;
    seeRoute: string;
    buyUseAgain: string;
    buyShare: string;
    buyBring: string;
    creatorPersonal: string;
    creatorProfessional: string;
    creatorEnterprise: string;
    creatorInternal: string;
    faq: string;
    faqStartPlaceQ: string;
    faqDurationQ: string;
    faqGiftQ: string;
    faqFreeQ: string;
    faqFrequencyQ: string;
    faqFriendsQ: string;
    faqStartTimeQ: string;
    faqStartPlaceRouteA: () => string;
    faqStartPlaceCollectionA: () => string;
    faqDurationRouteA: () => string;
    faqDurationCollectionA: () => string;
    faqGiftA: string;
    faqFreeA: string;
    faqFrequencyA: string;
    faqFrequencyFreeA: string;
    faqFriendsA: string;
    faqFriendsWithQuizA: string;
    faqStartTimeA: string;
    bringTime: () => string;
    workCollection: () => string;
    workRoute: () => string;
}
